import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../utils/supabase';
import { useAuth } from './useAuth';
import { 
  HIRED_NOTIFICATION_CONFIG, 
  NOTIFICATION_DEFAULTS, 
  DB_FIELD_MAPPING, 
  NOTIFICATION_TRIGGERS 
} from '../components/notifications/config';

// Development logging utility
const debugLog = (message, ...args) => {
  if (HIRED_NOTIFICATION_CONFIG.ERROR_HANDLING.LOG_ERRORS_IN_DEV && process.env.NODE_ENV === 'development') {
    console.log(`[HiredNotifications] ${message}`, ...args);
  }
};

export const useHiredNotifications = () => {
  const { user } = useAuth();
  const [pendingNotification, setPendingNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Memoize the query select string to prevent unnecessary re-execution
  const querySelect = useMemo(() => `
    id,
    status,
    acknowledged_at,
    jobs (
      title,
      user_profiles!jobs_employer_id_fkey (
        company_name,
        full_name
      )
    )
  `, []);

  // Check for new hired applications
  const checkForHiredNotifications = useCallback(async () => {
    if (!user?.id) {
      debugLog('No user found, skipping hired notifications check');
      return;
    }

    try {
      setIsLoading(true);
      debugLog('Checking for hired notifications for user:', user.id);

      // Get applications that have been accepted and not yet acknowledged
      const { data: hiredApplications, error } = await supabase
        .from('job_applications')
        .select(querySelect)
        .eq('applicant_id', user.id)
        .eq('status', NOTIFICATION_TRIGGERS.ACCEPTED)
        .is('acknowledged_at', null)
        .order('updated_at', { ascending: false })
        .limit(1);

      if (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error checking hired applications:', error);
        }
        // Don't throw in production - fail silently
        return;
      }

      debugLog('Query completed, found applications:', hiredApplications?.length || 0);

      if (hiredApplications && hiredApplications.length > 0) {
        const application = hiredApplications[0];
        debugLog('Found pending hired notification:', application.id);
        
        setPendingNotification({
          applicationId: application[DB_FIELD_MAPPING.JOB_APPLICATIONS.ID],
          jobTitle: application.jobs?.[DB_FIELD_MAPPING.JOBS.TITLE] || NOTIFICATION_DEFAULTS.JOB_TITLE,
          companyName: application.jobs?.user_profiles?.[DB_FIELD_MAPPING.USER_PROFILES.COMPANY_NAME] || 
                      application.jobs?.user_profiles?.[DB_FIELD_MAPPING.USER_PROFILES.FULL_NAME] || 
                      NOTIFICATION_DEFAULTS.COMPANY_NAME,
        });
      } else {
        debugLog('No pending hired notifications found');
        setPendingNotification(null);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error checking for hired notifications:', error);
      }
      // Fail silently in production
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, querySelect]);

  // Mark notification as acknowledged
  const acknowledgeHiredNotification = useCallback(async (applicationId) => {
    if (!applicationId) {
      debugLog('Error: No application ID provided for acknowledgment');
      return false;
    }

    try {
      debugLog('Acknowledging hired notification for application:', applicationId);
      
      const { error } = await supabase
        .from('job_applications')
        .update({ acknowledged_at: new Date().toISOString() })
        .eq('id', applicationId);

      if (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error acknowledging notification:', error);
        }
        return false;
      }

      debugLog('Notification acknowledged successfully');
      setPendingNotification(null);
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error acknowledging hired notification:', error);
      }
      return false;
    }
  }, []);

  // Check for notifications on mount and user change
  useEffect(() => {
    if (user?.id) {
      checkForHiredNotifications();
    }
  }, [user?.id, checkForHiredNotifications]);

  // Set up real-time subscription for application status changes
  useEffect(() => {
    if (!user?.id) return;

    debugLog('Setting up real-time subscription for user:', user.id);

    const channelName = `${HIRED_NOTIFICATION_CONFIG.SUBSCRIPTION.CHANNEL_PREFIX}${user.id}`;
    const subscription = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'job_applications',
          filter: `${DB_FIELD_MAPPING.JOB_APPLICATIONS.APPLICANT_ID}=eq.${user.id}`,
        },
        (payload) => {
          debugLog('Real-time update received:', payload.new?.[DB_FIELD_MAPPING.JOB_APPLICATIONS.STATUS]);
          // Check if status changed to accepted and wasn't acknowledged
          if (
            payload.new?.[DB_FIELD_MAPPING.JOB_APPLICATIONS.STATUS] === NOTIFICATION_TRIGGERS.ACCEPTED && 
            payload.old?.[DB_FIELD_MAPPING.JOB_APPLICATIONS.STATUS] !== NOTIFICATION_TRIGGERS.ACCEPTED &&
            !payload.new?.[DB_FIELD_MAPPING.JOB_APPLICATIONS.ACKNOWLEDGED_AT]
          ) {
            debugLog('New hire detected, checking for notifications');
            // Use configured delay to ensure database consistency
            setTimeout(() => checkForHiredNotifications(), HIRED_NOTIFICATION_CONFIG.PERFORMANCE.DEBOUNCE_CHECK_DELAY);
          }
        }
      )
      .subscribe();

    return () => {
      debugLog('Cleaning up real-time subscription');
      subscription.unsubscribe();
    };
  }, [user, checkForHiredNotifications]);

  return {
    pendingNotification,
    isLoading,
    acknowledgeHiredNotification,
    checkForHiredNotifications,
  };
};